import { Box, Button, Grid, Modal, Tab, Tabs, Typography } from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import React from 'react';
import { useState } from 'react';
import { exchangeRates } from 'src/const';
import {
  aggregateToMarketLevel,
  convertCurrency,
  findGreatestDate,
  formatCurrency,
} from 'src/utils/functions';
import CalendarLayout from './Calendar';
import CloseIcon from '@material-ui/icons/Close';
import { COLOR_SUCCESS_1, COLOR_SUCCESS_BACKGROUNG, COLOR_TEXT_1 } from 'src/colors';

const a11yProps = (index: number) => {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`,
  };
};

const ListingModal = ({
  open,
  onClose,
  rowData,
  advancedListings,
  listingsFutureData,
  currency,
}) => {
  const [selectedTab, setSelectedTab] = useState(0);
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setSelectedTab(newValue);
  };

  const CustomListingTabPanel = ({ value, index, children, rowData }) => {
    let selectedListingID = rowData?.listingID;
    let historicalAllData = advancedListings?.data || [];
    let futureAllDatas = listingsFutureData?.data || [];
    const historicalRows: any[] = [];
    const HistoryTableColumn: GridColDef[] = [
      { field: 'id', headerName: 'ID', width: 200, hide: true },
      {
        field: 'date',
        headerName: 'Date',
        width: 150,
        editable: false,
      },
      {
        field: 'availiableListingNights',
        headerName: 'Availiable Nights',
        width: 150,
        editable: false,
      },
      {
        field: 'bookedNights',
        headerName: 'Booked Nights',
        width: 150,
        editable: false,
      },
      {
        field: 'revenue',
        headerName: 'Revenue',
        width: 150,
        editable: false,
        renderCell: (params: any) => (
          <Typography>
            {exchangeRates[currency].symbol}
            {params.value != -1
              ? formatCurrency(String(convertCurrency(currency, params.value)))
              : 'N/A'}
          </Typography>
        ),
      },
      {
        field: 'avgOccupancy',
        headerName: 'Occupancy',
        width: 150,
        editable: false,
        renderCell: (params: any) => <Typography>{params.value}%</Typography>,
      },
      {
        field: 'adr',
        headerName: 'ADR',
        width: 150,
        editable: false,
        renderCell: (params: any) => (
          <Typography>
            {exchangeRates[currency].symbol}
            {params.value != -1
              ? formatCurrency(String(convertCurrency(currency, params.value)))
              : 'N/A'}
          </Typography>
        ),
      },
    ];

    let historicalData = historicalAllData.find(listing => listing.listingID === selectedListingID);
    let futureData = futureAllDatas.find(listing => listing.listingID === selectedListingID);
    let listingData = aggregateToMarketLevel([historicalData], [], false);
    let lastDate = '2025-05-25';
    if (futureData) {
      lastDate = findGreatestDate(futureData);
    }

    if (listingData) {
      let i = 0;
      for (let date of Object.keys(listingData)) {
        historicalRows.push({
          id: i,
          date: date,
          availiableListingNights: listingData[date]?.availiableListingNights,
          bookedNights: listingData[date]?.availiableListingNights,
          bookedNightsCount: listingData[date]?.bookedNightsCount,
          avgOccupancy: listingData[date]?.occupancyRates ? listingData[date]?.occupancyRates : 0,
          adr: listingData[date]?.adrRates ? listingData[date]?.adrRates : 0,
          revenue: listingData[date]?.revenue ? listingData[date]?.revenue : 0,
        });
        i += 1;
      }
    }

    return (
      <div
        role="tabpanel"
        hidden={value !== index}
        id={`simple-tabpanel-${index}`}
        aria-labelledby={`simple-tab-${index}`}
        style={{
          height: '100%',
          width: '100%',
          display: 'flex',
          justifyContent: 'center',
          paddingTop: '10px',
        }}
      >
        {value == 0 ? (
          <Box sx={{ height: '90%', width: '90%' }}>
            <DataGrid
              sortingOrder={['desc', 'asc']}
              sx={{ backgroundColor: 'white' }}
              rows={historicalRows}
              columns={HistoryTableColumn}
              disableColumnMenu={true}
            />
          </Box>
        ) : (
          <Box sx={{ height: '90%', width: '90%', overflow: 'scroll' }}>
            <CalendarLayout lastDate={lastDate} futureData={futureData} />
          </Box>
        )}
      </div>
    );
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Grid
        container
        item
        style={{
          backgroundColor: 'white',
          outline: 'none',
          height: '90%',
          position: 'relative',
          width: '80%',
          padding: '10px',
          borderRadius: '10px',
        }}
      >
        <Box sx={{ position: 'absolute', top: '10px', right: '10px', zIndex: 100 }}>
          <CloseIcon style={{ cursor: 'pointer', color: COLOR_TEXT_1 }} onClick={onClose} />
        </Box>
        <Box sx={{ position: 'absolute', top: '5px', right: '60px', zIndex: 100 }}>
          <Button
            variant="outlined"
            onClick={() => {
              const listingURL = `airbnb.com/rooms/${rowData?.listingID}`;
              window.open('https://www.' + listingURL, 'nofollow');
            }}
            sx={{
              paddingLeft: '10px',
              margin: '10px',
              width: '133px',
              backgroundColor: COLOR_SUCCESS_BACKGROUNG,
              color: COLOR_SUCCESS_1,
              borderColor: COLOR_SUCCESS_BACKGROUNG,
              '&:hover': {
                borderColor: COLOR_SUCCESS_1,
              },
            }}
          >
            View Listing
          </Button>
        </Box>
        <Box sx={{ height: '90%', width: '100%', overflow: 'hidden' }}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs value={selectedTab} onChange={handleTabChange} aria-label="basic tabs example">
              <Tab label="Historical" {...a11yProps(0)} />
              <Tab label="Future" {...a11yProps(1)} />
            </Tabs>
          </Box>
          <CustomListingTabPanel value={selectedTab} index={0} rowData={rowData}>
            Historical
          </CustomListingTabPanel>
          <CustomListingTabPanel value={selectedTab} index={1} rowData={rowData}>
            Future
          </CustomListingTabPanel>
        </Box>
      </Grid>
    </Modal>
  );
};
 export default ListingModal